/* eslint-disable @typescript-eslint/no-empty-object-type */
import { ComplexValue, CompositeKeys, LoadOptions } from 'app/types-shared';
import * as React from 'react';

import { ControlType, IteratorType } from './component-configuration';
import { ValidationProps } from 'ui';
import { HeaderVisibilityConfiguration } from 'app/components';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';

export interface IteratorConfig {
  type: IteratorType;
  defaultValues?: UiDefaultValueDefinition[];
}

////////////////////////////////////////////////////////////////////////////////////////////////////
//
// Now, all above should be interpreted by the UI Builder and rendered into a UI Schema

// A UI Builder is:
//  - a function that takes a UI Definition and selector/map function as input
//    and returns a strong typed UI schema to render

// The UI schema 
//  - will contain a concrete component constuctor function as Type

// The strategy for when UI Schema will be created and cached is: 
//  - build one schema per applicationId
//
////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * The root level UI Schema describing what to render based on configuration and runtime evaluations
 * 
 * @export
 * @interface UiSchema
 */
export interface UiSchema {
  contextId: number; //domainContext
  applicationId: number;
  id: string;
  children: ComponentDescriptor[]; // added by sequence
  availableSections: ComponentDescriptor[];
  getById(domainId:number):ComponentDescriptor;
}

/**
 * Describes how the component should render and behave
 * 
 * @export
 * @interface ComponentDescriptor
 */
export interface ComponentDescriptor {
  /**
   * Domain number from config 
   */
  id: number;     
  /**
   * Name of connected entity property whose value is represented by this component
   */                  
  dataTag: string;                  
  /**
   * Path of ancestor entities for this component. Left to right, highest to lowest level.
   */
  entityTypePath: string[];      
  /**
   * Translated label text. Resolved server side.
   */         
  label?: string;         
  /**
   * Child components. Order is determined server side.
   */          
  children?: ComponentDescriptor[]; 
  /**
   * strong typed attributes parsed (parsed per type) from UiDefinition.attributes
   */
  properties?: ComponentProps;      
  /**
   * Display/render order. Determined server side.
   */  
  sequence: number;                 
  /**
   * The react component that renders the visual representation
   */
  componentClass: any;              // The resolved react component
  /**
   * List of higher order components that the componentClass will be wrapped in.
   */
  enhancers?: ((componentClass: React.ComponentClass<any> | React.FC<any>, props?: InitialUiEngineProps) => 
    React.ComponentClass<any> | React.FC<any>)[];
  /**
   * Array of selectors providing extra props, usually selected from the provided state, to the components.
   */    
  selectors?: (( state: {}, // State slice for the current stateArea
                 props: ConnectedComponentProps, 
                 store: {} /* All of ApplicationState */) => {})[];
  /**
   * Friendly name of the React component. Resolved from name of [ControlType] enum.
   */                 
  componentName: string;            
  /**
   * Client side events the component would like to have callbacks provided by the ui engine
   */  
  componentEvents: string[];
  /**
   * Default props passed into the representing React component, independent of which component performs the rendering
   */  
  componentDefaultProps: Object;
  /**
   * Configuration for iterator components.
   */
  iterate?: IteratorConfig;
  /**
   * When this components value is a reference to a code. 
   * This is the id of the codetable where codes are loaded from.
   */  
  codetable?: number;
  /**
   * A descriptor on how to calculate or fetch the default value for component.
   */  
  defaultValue?: DefaultValueDefinition;
}

export interface DefaultValueDefinition {
  /**
   * id of the function that should handle creating this default value.
   * The existence of the other parameters often relies on this
   */
  valueFunction: DefaultValueFunction;
  /**
   * the default value, if available
   */
  value?: number | CodetableWireFormat | string;
  /**
   * expression to evaluate 
   */
  dateRegex?: string;
  /**
   * if the expression cannot be evaluated or is empty, an API call must
   * be performed to calculate the value
   */
  dbFunction?: number;
}

export enum DefaultValueFunction {
  codeValue = 'codeValue',
  dateValue = 'dateValue'
}

export type UiDefaultValueDefinition = {dataTag: string; defaultValue: DefaultValueDefinition};

export interface ComponentCallbacks {
  [indexer: string]: Function;
}

/**
 * Configuration attributes mapped as strong typed props of the resolved component
 * 
 * @export
 * @interface ComponentProps
 */
export interface ComponentProps {
  [indexer: string]: boolean | number | string;
}

/**
 * Describes all default settings for a component that is plugged into the ui engine
 * 
 * @export
 * @interface PropertyBuilderMap
 */
export interface PropertyBuilderMap {
  [index: number]: { attributeKey: string; builder?: Function };
}

export interface ComponentRegistry {
  components: ComponentSet[];
}

export interface ComponentSet {
  mode: number;
  parentMode?: number;
  components: ComponentIndex;
}

export interface ComponentIndex {
  [indexer: number]: ComponentType;
}

export interface ComponentType {
  component: any;
  selectors?: ((state: {}, props: any, fullState?: {}) => ( {[indexer: string]: any}) )[];
  enhancers?: ((componentClass: React.ComponentClass<any> | React.FC<any>, props?: any) => React.ComponentClass<any> | React.FC<any>)[];
  iterate?: { type: IteratorType };
  configType: ControlType;
  events?: string[];
}
/**
 * Describes the current entity data context, and rendermode
 */
export interface ComponentContext {
  path: CompositeKeys;
  applicationId?: number;
  mode?: number;
}

export interface ChildComponentContext extends ComponentContext {
  primaryKey: ComplexValue<number>;
  /** 
   * A child component lives within a context that is created by the iterator. 
   * 
   * When an iterator is just rendering one component per entity in the subtable,
   * this flag would be false, because it maps to a physical entity.
   * 
   * When an iterator uses another datasource than the entities in a subtable, such as a list of codes
   * it is creating "virtual" contexts to render the components.
   * 
   * This flag simply indicates that there is not (or not yet) an associated entity in the data, and the component
   * may have to take action, such as adding the entity itself, before it can interact with the data.
   */
  virtual: boolean;
  /**
   * Children may need to add the physical item, so they need to know the default values for their context.
   */
  defaultValues: UiDefaultValueDefinition[];
}

export interface ComponentMetadata {
  readOnly?: boolean;
  requiredField?: boolean;
  hidden?: boolean;
  validation?: ValidationProps;
  validationDescendants?: ValidationProps[];
}

export type ComponentWrapper = (component: React.Component<any, any>, props: ConnectedComponentProps) => any;

/**
 * The format that ui-engine uses when it passes async loaded data into a component
 */
export interface ComponentDataShape {
  options: ComplexValue<any>[];
  loadOptions: LoadOptions;
  loadOptionsError: boolean;
  fetching: boolean;
  categoryOptions: ComplexValue<any>[];
  loadCategoryOptions: LoadOptions;
  loadCategoryOptionsError: boolean;
  fetchingCategories: boolean;
}

export interface ObjectMap{
  [indexer: string]: any;
}
/**
 * Configuration props passed as configuration to initialize ui-engine
 */
export interface InitialUiEngineProps {
  componentDescriptor?: ComponentDescriptor;

  /**
   * Only passed in for components rendered in an iterator.
   * This will be the ComponentDescriptor representing the details.
   */
  detailsViewDescriptor?: ComponentDescriptor;
  /**
   * Iterators pass an explicit resolvecontext downwards
   */
  resolveContext?: ComponentContext; 
  /**
   * Reconnect is a flag to re-build the whole Virtual DOM tree
   */
  reconnect?: boolean;
  /**
   * Path, in store, to start when resolving entity data.
   */
  statePath: string | string[];
  /**
   * Action creators passed in from the outermost host container for a Field
   */
  actions: {};
  intl?: any;
  entityId?: number;
  entityStoreKey: string;
  /**
   * Case number of the entity (if it exists)
   * DEPRECATED, instead use hostProps to resolve
   */
  caseNumber?: number;
  /**
   * Application id for the current entity.
   */
   applicationId?: number;
  /**
   * Configuration to be used by "header" children of the host.
   */
  headerVisibilityConfiguration?: HeaderVisibilityConfiguration;
  /**
   * Explicitly enabling/disabling of add and remove for iterator scenarios
   */
  enabledChildEntityOperations?: EnabledChildEntityOperations;
  /**
   * Updated props as a lazy resolver, we should gracefully migrate the above properties to be 
   * returned by this function instead.
   */
  hostProps?: () => InitialUiEngineProps;

  /**
   * For case-listings. Some configuration/behaviour depends on knowing the report used when rendering a case.
   */
  selectedReport?: number;
}
/**
 * Components connected to ui-engine will have these props spread onto them
 */
export interface ConnectedComponentProps
{
  actions?: ObjectMap;

  componentId: string;
  className?: string;
  componentName: string;
  componentDescriptor?: ComponentDescriptor;
  
  /**
   * Explicitly enabling/disabling of add and remove for iterator scenarios
   */
  enabledChildEntityOperations?: EnabledChildEntityOperations;

  formatDate: (dt: Date) => string;
  formatTime: (dt: Date) => string;
  formatNumber: (num: number) => string;
  parseAsDateUTC: (dateString: string) => Date;
  cloneDate: (localDate: Date) => Date;
  createUTCDate(dateValues: {
    year?: number; 
    month?: number; 
    day?: number; 
    hours?: number; 
    minutes?: number;
    seconds?: number;
    milliseconds?: number;
  });

  intl: ObjectMap;
  
  //TODO These should be grouped and have type ComponentDataShape
  options?: ComplexValue<any>[];
  loadOptions?: ObjectMap; 
  fetching?: boolean;

  categoryOptions?: ComplexValue<any>[];
  loadCategoryOptions?: ObjectMap;
  fetchingCategories: boolean;

  statePath: string | string[];
  hostProps: () => ObjectMap;   
  locale: string;
  resolveContext: ComponentContext;
  sequence?: number;

  onSelectDocumentItem?: (itemKey?: CompositeKeys) => void;
  onAddDocumentItem?: (initialValues?: ObjectMap) => void;
  onDeleteDocumentItem?: () => void;
  onEditDocument?: () => void;
  onDeleteDocument?: () => void;
  onDuplicateDocument?: () => void;
  onChange?: (value: string | Date | number | ComplexValue<number>) => void;
  onBlur?: () => void;
  onLoadOptions?: (search?: LoadOptions) => void;
  onLoadBowtie: () => void;

  // Other, dynamic stuff
  [index: string] : any;

}

export interface EnabledChildEntityOperations{
  add: boolean;
  remove: boolean;
  copy?: boolean;
}
